import { useNavigate, useOutletContext } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  getPendingReportsByDoctor,
  getSignedReportsReadyToSend,
  getPatientById,
  examReportsData
} from "@/data/mockData";
import {
  FileText,
  Clock,
  CheckCircle2,
  AlertTriangle,
  ChevronRight,
  Send,
  User,
  Calendar
} from "lucide-react";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { it } from "date-fns/locale";

export const DoctorDashboard = () => {
  const navigate = useNavigate();
  const { currentDoctor } = useOutletContext();
  
  const pendingReports = getPendingReportsByDoctor(currentDoctor.id);
  const signedReports = getSignedReportsReadyToSend().filter(r => r.doctorId === currentDoctor.id);
  const completedToday = examReportsData.filter(
    r => r.doctorId === currentDoctor.id && 
    r.status === 'sent' && 
    r.sentAt?.startsWith('2026-01-27')
  ).length;

  const urgentCount = pendingReports.filter(r => r.priority === 'urgent').length;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold">Dashboard</h1>
          <p className="text-muted-foreground">Benvenuto, {currentDoctor.name}</p>
        </div>
        <Button
          variant="premium"
          onClick={() => navigate(`/booking?from=specialist&specialistId=${currentDoctor.id}`)}
          data-testid="doctor-new-booking-btn"
          className="gap-2"
        >
          <Calendar className="h-4 w-4" />
          Prenota visita paziente
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card className={cn(pendingReports.length > 0 && "border-warning/50")}>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className={cn(
                "h-12 w-12 rounded-xl flex items-center justify-center",
                pendingReports.length > 0 ? "bg-warning/10" : "bg-muted"
              )}>
                <FileText className={cn(
                  "h-6 w-6",
                  pendingReports.length > 0 ? "text-warning" : "text-muted-foreground"
                )} />
              </div>
              <div>
                <p className="text-2xl font-bold">{pendingReports.length}</p>
                <p className="text-sm text-muted-foreground">Da Refertare</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={cn(urgentCount > 0 && "border-destructive/50")}>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className={cn(
                "h-12 w-12 rounded-xl flex items-center justify-center",
                urgentCount > 0 ? "bg-destructive/10" : "bg-muted"
              )}>
                <AlertTriangle className={cn(
                  "h-6 w-6",
                  urgentCount > 0 ? "text-destructive" : "text-muted-foreground"
                )} />
              </div>
              <div>
                <p className="text-2xl font-bold">{urgentCount}</p>
                <p className="text-sm text-muted-foreground">Urgenti</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-xl bg-info/10 flex items-center justify-center">
                <Send className="h-6 w-6 text-info" />
              </div>
              <div>
                <p className="text-2xl font-bold">{signedReports.length}</p>
                <p className="text-sm text-muted-foreground">Da Inviare</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-xl bg-success/10 flex items-center justify-center">
                <CheckCircle2 className="h-6 w-6 text-success" />
              </div>
              <div>
                <p className="text-2xl font-bold">{completedToday}</p>
                <p className="text-sm text-muted-foreground">Completati Oggi</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Pending Reports */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-warning" />
                  Esami da Refertare
                </CardTitle>
                <CardDescription>Ordinati per priorità e data</CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={() => navigate("/doctor/pending")}>
                Vedi tutti
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <ScrollArea className="h-[300px]">
              <div className="px-6 pb-6 space-y-3">
                {pendingReports.length === 0 ? (
                  <div className="text-center py-8">
                    <CheckCircle2 className="h-12 w-12 mx-auto text-success/50 mb-4" />
                    <p className="text-muted-foreground">Nessun esame in attesa</p>
                  </div>
                ) : (
                  pendingReports.slice(0, 5).map((report) => {
                    const patient = getPatientById(report.patientId);
                    return (
                      <div
                        key={report.id}
                        className={cn(
                          "p-3 rounded-lg border cursor-pointer transition-all hover:shadow-md",
                          report.priority === 'urgent' && "border-destructive/50 bg-destructive/5"
                        )}
                        onClick={() => navigate(`/doctor/report/${report.id}`)}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            {report.priority === 'urgent' && (
                              <Badge variant="destructive" className="gap-1">
                                <AlertTriangle className="h-3 w-3" />
                                Urgente
                              </Badge>
                            )}
                            <Badge variant="outline">{report.serviceName}</Badge>
                          </div>
                          <span className="text-xs text-muted-foreground">
                            {format(new Date(report.examDate), "dd/MM HH:mm")}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">{patient?.name}</span>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Ready to Send */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Send className="h-5 w-5 text-info" />
                  Pronti per l'Invio
                </CardTitle>
                <CardDescription>Referti firmati da inviare</CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={() => navigate("/doctor/completed")}>
                Vedi tutti
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <ScrollArea className="h-[300px]">
              <div className="px-6 pb-6 space-y-3">
                {signedReports.length === 0 ? (
                  <div className="text-center py-8">
                    <Send className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                    <p className="text-muted-foreground">Nessun referto da inviare</p>
                  </div>
                ) : (
                  signedReports.map((report) => {
                    const patient = getPatientById(report.patientId);
                    return (
                      <div
                        key={report.id}
                        className="p-3 rounded-lg border border-success/30 bg-success/5 cursor-pointer transition-all hover:shadow-md"
                        onClick={() => navigate(`/doctor/report/${report.id}`)}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <Badge className="bg-success/10 text-success border-success/20 gap-1">
                            <CheckCircle2 className="h-3 w-3" />
                            Firmato
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            {format(new Date(report.signature.signedAt), "dd/MM HH:mm")}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4 text-muted-foreground" />
                            <span className="font-medium">{patient?.name}</span>
                          </div>
                          <Badge variant="outline">{report.serviceName}</Badge>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DoctorDashboard;
